import { v4 as uuidv4 } from 'uuid'
import type { SyncData, PlatformInfo, AccountInfo } from '../types/sync'

// Message types matching backer-ctrl's external.ts
export interface ExtensionRequest<T> {
  type: 'request'
  traceId: string
  action: string
  data?: T
}

export interface ExtensionResponse<T> {
  type: 'response'
  traceId: string
  action: string
  code: number
  message: string
  data: T
}

// Send a request to the backer-ctrl extension
export async function sendExtensionRequest<T>(action: string, data?: unknown, timeout: number = 5000): Promise<T> {
  const traceId = uuidv4()
  console.log(`[ExtensionBridge] Sending request: ${action}`, { traceId, data })

  return new Promise<T>((resolve, reject) => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined

    const messageHandler = (event: MessageEvent) => {
      console.log(`[ExtensionBridge] Received message:`, event.data)
      if (
        event.data.type === 'response' &&
        event.data.action === action &&
        event.data.traceId === traceId
      ) {
        cleanup()
        if (event.data.code === 0) {
          console.log(`[ExtensionBridge] Request successful:`, event.data.data)
          resolve(event.data.data)
        } else {
          console.error(`[ExtensionBridge] Request failed:`, event.data.message)
          reject(new Error(event.data.message || 'Extension error'))
        }
      }
    }

    const cleanup = () => {
      window.removeEventListener('message', messageHandler)
      if (timeoutId) clearTimeout(timeoutId)
    }

    if (timeout > 0) {
      timeoutId = setTimeout(() => {
        cleanup()
        console.error(`[ExtensionBridge] Request timeout after ${timeout}ms`)
        reject(new Error(`Request timeout after ${timeout}ms`))
      }, timeout)
    }

    window.addEventListener('message', messageHandler)
    window.postMessage({ type: 'request', traceId, action, data }, '*')
  })
}

// Check if the extension is installed and running
export async function checkExtensionStatus(): Promise<boolean> {
  try {
    // Try multiple times with increasing timeout
    for (let i = 0; i < 3; i++) {
      try {
        await sendExtensionRequest('MULTIPOST_EXTENSION_CHECK_SERVICE_STATUS', undefined, 2000 + i * 1000)
        return true
      } catch {
        if (i === 2) return false
        await new Promise(resolve => setTimeout(resolve, 500))
      }
    }
    return false
  } catch {
    return false
  }
}

// Get all available platforms from the extension
export async function getPlatformsFromExtension(): Promise<PlatformInfo[]> {
  try {
    const response = await sendExtensionRequest<{ platforms: PlatformInfo[] }>('MULTIPOST_EXTENSION_PLATFORMS')
    return response?.platforms || []
  } catch {
    return []
  }
}

// Get account info from the extension
export async function getAccountInfosFromExtension(): Promise<Record<string, AccountInfo>> {
  try {
    const response = await sendExtensionRequest<{ accountInfo: Record<string, AccountInfo> }>('MULTIPOST_EXTENSION_GET_ACCOUNT_INFOS')
    return response?.accountInfo || {}
  } catch {
    return {}
  }
}

// Trigger publishing through the extension
export async function publishToExtension(syncData: SyncData): Promise<void> {
  await sendExtensionRequest('MULTIPOST_EXTENSION_PUBLISH', syncData)
}

// Open extension options
export async function openExtensionOptions(): Promise<void> {
  await sendExtensionRequest('MULTIPOST_EXTENSION_OPEN_OPTIONS')
}

// Refresh account infos
export async function refreshAccountInfos(): Promise<void> {
  await sendExtensionRequest('MULTIPOST_EXTENSION_REFRESH_ACCOUNT_INFOS', { isFocused: true })
}

// Request trust domain
export async function requestTrustDomain(): Promise<boolean> {
  try {
    const response = await sendExtensionRequest<{ trusted: boolean }>('MULTIPOST_EXTENSION_REQUEST_TRUST_DOMAIN', undefined, 30000)
    return response?.trusted || false
  } catch {
    return false
  }
}
